/**
 * main.ts — client entry point: route, join, then run the render loop.
 *
 * `/room/:code` joins that room directly (add `?name=…` to skip the prompt);
 * any other path shows the landing form. The arena renders behind both.
 *
 * The loop does four things: pump input on a fixed timestep, let renderSync
 * interpolate the snapshot stream, point the camera at the local player, and draw.
 * No gameplay logic runs here — the server owns all of it.
 *
 * Outbound commands go through the predictor on the way out (`predict.ts`), which
 * is the only reason the camera and your own blade keep up with the mouse: the
 * position renderSync reports for the local player is the predicted one.
 *
 * Input is pumped from THIS loop rather than a `setInterval`, and that is a feel
 * decision, not a tidiness one — see `pumpInput`.
 */

import * as THREE from 'three';
import {
  loadoutWeight,
  unpackLoadout,
  FEEL,
  JoinError,
  NO_WINNER,
  isValidRoomCode,
  normalizeRoomCode,
  sanitizeDisplayName,
  tickMs,
  type InputCommand,
  type PlayerInfo,
  type RoundMsg,
} from '@dueler/shared';
import { followCamera } from './camera.js';
import { createEffects } from './effects.js';
import { createCombatHud, type CombatHud } from './ui/combatHud.js';
import { isTextEntryTarget, onKeyPress, onKeyToggle, readDebugOptions } from './debug.js';
import { createTrajectoryView, type TrajectoryView } from './debug/trajectoryView.js';
import { createInput } from './input.js';
import { joinRoom, type Session } from './net.js';
import { createPredictor, type Predictor } from './predict.js';
import { createRenderSync, type RenderSync } from './renderSync.js';
import { createStage } from './scene.js';
import { createSparks } from './sparks.js';
import { createAudio, place, OWN_BODY, type Placement } from './audio.js';
// The HUD's own stylesheet in index.html now asks for these faces too, not just
// the landing and the armory, so the app declares the dependency once at the top
// rather than relying on whichever view happened to pull them in.
import './ui/vendor/fonts.css';
import { createChat, type Chat } from './ui/chat.js';
import { createHud, type Hud } from './ui/hud.js';
import { createScoreboard, type Scoreboard } from './ui/scoreboard.js';
import { createArmory, type Armory } from './ui/armory.js';
import { showLanding } from './ui/landing.js';

const debug = readDebugOptions();
const effects = createEffects();
const stage = createStage(document.body);
// Sparks belong to the arena, not to a session: they outlive a rejoin and cost
// nothing while idle.
const sparks = createSparks(stage.scene);
// Synthesized on demand, so this costs nothing until something makes a noise.
// Browsers will not start an audio clock without a gesture, so it is unlocked by
// the same click that takes pointer lock.
const audio = createAudio();
if (debug.volume !== undefined) audio.setVolume(debug.volume);
window.addEventListener('pointerdown', () => audio.unlock());

/**
 * The combat sounds in order, for the N key. Every one of these is synthesized
 * from first principles rather than recorded, so there is no way to check the
 * balance except to hear them side by side — which is exactly what this is for.
 */
const AUDITION: readonly { name: string; play: () => void }[] = [
  { name: 'swing (slash)', play: () => audio.swing('slash', OWN_BODY) },
  { name: 'swing (stab)', play: () => audio.swing('stab', OWN_BODY) },
  { name: 'hit — light', play: () => audio.hit(FEEL.V_REF * 0.4, OWN_BODY) },
  { name: 'hit — hard', play: () => audio.hit(FEEL.V_REF * 2, OWN_BODY) },
  { name: 'block — light', play: () => audio.block(FEEL.V_REF * 0.4, OWN_BODY) },
  { name: 'block — hard', play: () => audio.block(FEEL.V_REF * 2, OWN_BODY) },
  { name: 'parry', play: () => audio.parry(FEEL.V_REF * 1.5, OWN_BODY) },
  { name: 'guard up', play: () => audio.guard(true, OWN_BODY) },
  { name: 'guard down', play: () => audio.guard(false, OWN_BODY) },
];
let auditionAt = 0;
const input = createInput(stage.renderer.domElement);
window.addEventListener('resize', () => stage.resize());

/** Set once the player is in a room; until then only the empty arena renders. */
interface ActiveSession {
  renderSync: RenderSync;
  hud: Hud;
  chat: Chat;
  combatHud: CombatHud;
  scoreboard: Scoreboard;
  selfSlot: () => number;
  trajectories: TrajectoryView;
  players: Map<number, PlayerInfo>;
  predictor: Predictor;
  armory: Armory;
}
let active: ActiveSession | undefined;
/** V toggles between first and third person. */
let firstPerson = false;

/**
 * The live session's input sink, set once joined. Commands are pumped from the
 * frame loop rather than a timer — see `pumpInput`.
 */
let sending: ((command: InputCommand) => void) | undefined;
/**
 * Real-clock time of the last pump, and the unspent remainder of a tick.
 * Undefined rather than 0 while unset: zero is a real `performance.now()` value.
 */
let lastPumpAt: number | undefined;
let pumpAcc = 0;
/** Render-clock instant of the most recently produced tick, for sub-tick smoothing. */
let simTime = 0;
/** Real-clock time of the previous frame, for the camera's free-look return. */
let lastFrameAt: number | undefined;
/**
 * Last reported free-look state, so the change can be announced. Alt toggles a
 * MODE, and a mode where the mouse stops turning your character is one a player can
 * sit in wondering what broke — it has to say so.
 */
let freeLookShown = false;
/**
 * Most ticks worth catching up on in one frame. A backgrounded tab banks seconds
 * of real time; replaying all of it at once is input the player never gave.
 */
const MAX_PUMP_CATCHUP = 4;

// Scratch basis vectors: extracting a basis allocates otherwise, and this runs
// once per sound.
const listenerRight = new THREE.Vector3();
const scratchUp = new THREE.Vector3();
const scratchBack = new THREE.Vector3();
const contactPoint = new THREE.Vector3();

/**
 * Stereo placement for a world point, taken from the camera. The camera is the
 * listener in both view modes — in third person the ear sits behind the shoulder
 * with the eye, which is what the player is reading the fight from.
 */
function placeAt(at: THREE.Vector3): Placement {
  stage.camera.matrixWorld.extractBasis(listenerRight, scratchUp, scratchBack);
  return place(stage.camera.position, listenerRight, at);
}

function frame(now: number): void {
  // Hitstop freezes the render clock, never the simulation.
  const render = effects.renderTime(now);
  // Render time, so bursts and torch flicker freeze with everything else
  // during hitstop.
  sparks.update(render);
  stage.update(render);

  if (active !== undefined) {
    active.hud.tickFrame(now);
    // Input first: the predictor must be stepped up to this instant before
    // renderSync asks it where the local player is, or the view is always one
    // frame behind the commands that produced it.
    // The body turns heavy while a swing is in flight. Read from the predictor,
    // which is the one place that knows whether the LOCAL player is mid-swing
    // without waiting for a snapshot to say so.
    input.setAttacking(active.predictor.attacking);
    pumpInput(now, render, active.predictor);
    active.renderSync.update(render);
    // Real time, not render time: the turntable should keep turning through a
    // hitstop, because the panel is not part of the fight.
    active.armory.update(now);

    // Free look eases the camera back once Alt is released, so it needs a frame
    // delta. Real time, not render time: the camera returning is not a hitstop
    // effect, and freezing it mid-return would look like a stall.
    input.update(lastFrameAt === undefined ? 0 : Math.max(0, now - lastFrameAt) / 1000);
    lastFrameAt = now;

    if (input.freeLook !== freeLookShown) {
      freeLookShown = input.freeLook;
      active.chat.system(freeLookShown ? 'free look on — Alt to exit' : 'free look off');
    }

    const self = active.selfSlot();
    const focus = active.renderSync.positionOf(self);
    if (focus) {
      // The camera reads the LOOK angles, which are the body's plus the free-look
      // offset. Everything that decides gameplay keeps reading the body's.
      followCamera(stage.camera, focus, input.lookYaw, input.lookPitch, firstPerson);
      // A wider field of view in first person: at arm's length a 70-degree
      // frustum crops the blade out of frame exactly when you need to see it.
      const fov = firstPerson ? FEEL.FOV_FIRST_PERSON : FEEL.FOV_THIRD_PERSON;
      if (stage.camera.fov !== fov) {
        stage.camera.fov = fov;
        stage.camera.updateProjectionMatrix();
      }
      effects.applyShake(stage.camera, now);
    }

    // The trajectory preview hangs off the local player's root transform, the
    // same composition combat uses: world = root × roll(dial) × bakedSample.
    const yaw = active.renderSync.yawOf(self);
    if (focus && yaw !== undefined) active.trajectories.update(render, focus, yaw, input.dial);

    const latest = active.renderSync.latest;
    const positions = new Map<number, THREE.Vector3>();
    const healths = new Map<number, number>();
    for (const player of latest?.players ?? []) {
      const pos = active.renderSync.positionOf(player.id);
      if (pos) positions.set(player.id, pos);
      healths.set(player.id, player.health);
    }
    active.combatHud.update(now, stage.camera, positions, healths, active.players, self, input.dial);
    // The real clock, not the hitstop-frozen one: an intermission counts down in
    // wall time regardless of what the render clock is doing.
    active.scoreboard.update(now, active.players, self);

    active.hud.setDebug({
      tick: latest?.tick ?? 0,
      seq: latest?.seq ?? 0,
      players: latest?.players.length ?? 0,
      lagMs: debug.lagMs,
      interpolating: active.renderSync.interpolating,
      pointerLocked: input.pointerLocked,
      predicting: debug.predict,
      // Unconfirmed commands and the size of the last correction: this is how you
      // tell "prediction is working" from "prediction is fighting the server"
      // without guessing.
      pendingInputs: active.predictor.pending,
      predictErrorM: active.predictor.correction,
      freeLook: input.freeLook,
    });
  }

  stage.render();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

/** Reads `/room/:code` and an optional `?name=` from the URL. */
function readRoute(): { code?: string; name?: string } {
  const match = /^\/room\/([^/]+)\/?$/.exec(location.pathname);
  const code = match?.[1] !== undefined ? normalizeRoomCode(decodeURIComponent(match[1])) : '';
  const name = new URLSearchParams(location.search).get('name');
  return {
    ...(isValidRoomCode(code) ? { code } : {}),
    ...(name !== null ? { name: sanitizeDisplayName(name) } : {}),
  };
}

function joinErrorText(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  if (message.includes(JoinError.RoomFull)) return 'That room is full (8 players).';
  if (message.includes(JoinError.BadCode)) return 'That room code is not valid.';
  return `Could not join: ${message}`;
}

async function run(): Promise<void> {
  const route = readRoute();
  let prefill: { code?: string; name?: string; error?: string } = {
    ...(route.code !== undefined ? { code: route.code } : {}),
    ...(route.name !== undefined ? { name: route.name } : {}),
  };

  // Direct link with a name attached joins straight away; otherwise ask.
  let credentials =
    route.code !== undefined && route.name !== undefined
      ? { code: route.code, name: route.name }
      : await showLanding(prefill);

  for (;;) {
    // Keep the URL shareable and drop the name from it.
    const query = debug.lagMs > 0 ? `?lag=${debug.lagMs}` : '';
    history.replaceState(null, '', `/room/${credentials.code}${query}`);

    const hud = createHud();
    const combatHud = createCombatHud();
    const scoreboard = createScoreboard();
    // Declared before the overlays that close over it: chat and the wardrobe both
    // send on a session that does not exist until the join below succeeds.
    let session: Session | undefined;
    // Chat owns the event log, so it is built before anything that logs. Opening
    // it must suppress gameplay input outright — see `input.setTyping`.
    const chat = createChat({
      onSay: (text) => session?.sendSay({ text }),
      onTypingChange: (typing) => input.setTyping(typing),
    });
    const armory = createArmory((kit) => {
      session?.sendLoadout({ kit });
      chat.system(`equipping ${loadoutWeight(kit).toFixed(1)}kg of kit`);
    });
    // Mirror of the server roster, maintained purely from reliable events.
    const players = new Map<number, PlayerInfo>();
    let selfSlot = -1;
    /** Last round message, to tell a phase change apart from a score change. */
    let lastRound: RoundMsg | undefined;

    // Prediction for the local player only. `?predict=0` withholds the override
    // and renderSync interpolates you like anyone else, which is how the two are
    // compared honestly.
    const predictor = createPredictor();
    const renderSync = createRenderSync(
      stage.scene,
      () => selfSlot,
      debug.predict ? (now) => predictor.view(now) : undefined,
      // Swings and guards are sounded from what is drawn, so a predicted swing is
      // heard the instant it is thrown. Contacts come from the server instead —
      // see onCombat.
      (event) => {
        // Your own swing and guard are your own body: centred, full volume, and
        // independent of where the camera happens to be looking. Everyone else's
        // is placed in the world, because which side it comes from is information
        // you need.
        const at = event.slot === selfSlot ? OWN_BODY : placeAt(event.pos);
        if (event.t === 'swing') audio.swing(event.clip, at);
        else audio.guard(event.up, at);
      },
      stage.camera,
    );

    try {
      session = await joinRoom(
        { ...credentials, lagMs: debug.lagMs },
        {
          onWelcome: (msg) => {
            selfSlot = msg.selfSlot;
            for (const p of msg.players) players.set(p.slot, p);
            hud.setRoom(msg.code, msg.selfSlot);
            hud.setRoster([...players.values()]);
            chat.system(`you joined as ${credentials.name} (slot ${msg.selfSlot})`);
            for (const p of msg.players) {
              if (p.slot !== msg.selfSlot) chat.system(`${p.name} is here`);
            }
          },
          onPlayerJoined: (msg) => {
            players.set(msg.player.slot, msg.player);
            hud.setRoster([...players.values()]);
            chat.system(`${msg.player.name} joined`);
          },
          onPlayerLeft: (msg) => {
            players.delete(msg.slot);
            hud.setRoster([...players.values()]);
            chat.system(`${msg.name} ${msg.reason === 'left' ? 'left' : 'disconnected'}`);
          },
          onSnapshot: (snapshot) => {
            // Reconcile before ingesting: the predictor decides where the local
            // player is, renderSync interpolates everyone else from the same
            // snapshot.
            predictor.reconcile(snapshot, selfSlot, performance.now());
            renderSync.ingest(snapshot);
            // The server decides what you are wearing, so the panel follows the
            // snapshot rather than its own click. A refused or corrected equip
            // shows up here as the selection quietly reverting.
            const self = snapshot.players.find((p) => p.id === selfSlot);
            if (self !== undefined) armory.setEquipped(unpackLoadout(self.kit));
          },
          onCombat: (msg) => {
            const now = performance.now();
            for (const event of msg.events) {
              combatHud.onEvent(event, now, selfSlot, (slot) => players.get(slot)?.name ?? `slot ${slot}`);

              // A chamber is symmetric — there is no attacker — so it gets its
              // own branch rather than being squeezed into the hit shape.
              if (event.t === 'chamber') {
                sparks.burst(event, 'chamber', 1);
                if (event.a === selfSlot || event.b === selfSlot) {
                  effects.impact(FEEL.HITSTOP_CHAMBER_MS, 1, now);
                }
                continue;
              }

              // Sparks fly for EVERY contact in the arena, not only your own:
              // a clash across the field is worth seeing.
              if (event.t === 'hit' || event.t === 'block' || event.t === 'parry') {
                const force = Math.min(1, event.speed / (FEEL.V_REF * 2));
                sparks.burst(
                  event,
                  event.t === 'hit' ? 'flesh' : 'steel',
                  // A parry is the cleanest contact there is; make it the
                  // brightest thing on screen.
                  event.t === 'parry' ? Math.max(0.85, force) : force,
                );

                // Contacts are sounded from the SERVER's events, never predicted:
                // whether steel met anything is the one thing the client must not
                // guess at, because a phantom clang is worse feedback than a late
                // one. Placed at the contact point, so a clash across the arena
                // comes from the right side.
                const at = placeAt(contactPoint.set(event.x, event.y, event.z));
                if (event.t === 'hit') audio.hit(event.speed, at);
                else if (event.t === 'block') audio.block(event.speed, at);
                else audio.parry(event.speed, at);
              }

              // Successful defend → open the local riposte window so the counter
              // predicts at the same speed the server will accept.
              if (
                (event.t === 'block' || event.t === 'parry') &&
                event.target === selfSlot
              ) {
                predictor.openRiposteWindow();
              }

              // Only hits you are part of are worth stopping the world for.
              const involved =
                (event.t === 'hit' || event.t === 'block' || event.t === 'parry') &&
                (event.attacker === selfSlot || event.target === selfSlot);
              if (!involved) continue;
              const blocked = event.t !== 'hit';
              effects.impact(
                blocked ? FEEL.HITSTOP_BLOCK_MS : FEEL.HITSTOP_FLESH_MS,
                Math.min(1, event.speed / (FEEL.V_REF * 2)),
                now,
              );
            }
          },
          onRound: (msg) => {
            const now = performance.now();
            const previous = lastRound;
            lastRound = msg;
            scoreboard.setRound(msg, now);
            if (msg.phase === previous?.phase && msg.round === previous.round) return;

            // Only transitions are worth a log line; the same message also
            // carries every score change.
            if (msg.phase === 'fighting') chat.system(`round ${msg.round} — fight`);
            else if (msg.phase === 'intermission') {
              const who =
                msg.winner === NO_WINNER
                  ? 'nobody'
                  : msg.winner === selfSlot
                    ? 'you'
                    : (players.get(msg.winner)?.name ?? `slot ${msg.winner}`);
              chat.system(`round ${msg.round} to ${who}`);
            } else chat.system('warming up — waiting for another duelist');
          },
          // The server attributes the line, so this trusts the slot it was given
          // for "was that me" and nothing else.
          onChat: (msg) => chat.said(msg, msg.slot === selfSlot),
          onTuned: (msg) => chat.system(`${msg.path} = ${msg.value}`),
          onDisconnect: (code) => {
            // Nothing left to reconcile against: predicting on would walk a ghost
            // around the arena.
            predictor.reset();
            chat.system(`disconnected from server (${code})`);
          },
        },
      );

      const trajectories = createTrajectoryView(stage.scene);
      active = {
        armory,
        renderSync,
        hud,
        chat,
        combatHud,
        scoreboard,
        selfSlot: () => selfSlot,
        trajectories,
        players,
        predictor,
      };
      // The frame loop pumps from here on; `active` is set, so the next frame
      // starts producing ticks.
      sending = session.sendInput;
      lastPumpAt = undefined;
      pumpAcc = 0;
      bindDebugKeys(renderSync, hud, chat, trajectories);
      onKeyPress('KeyI', () => armory.toggle());
      bindScoreboardKey(scoreboard);
      chat.system(
        'click to lock — WASD move, LMB slash, scroll up stab, scroll down overhead, RMB block (then LMB to riposte), Q feint, Z/mouse4 flourish, B/mouse5 bow, Alt free look, I armory, Tab scores, Y chat',
      );
      return;
    } catch (err) {
      renderSync.dispose();
      combatHud.dispose();
      scoreboard.dispose();
      armory.dispose();
      chat.dispose();
      hud.dispose();
      prefill = { code: credentials.code, name: credentials.name, error: joinErrorText(err) };
      credentials = await showLanding(prefill);
    }
  }
}

/**
 * Produces exactly one input command per elapsed tick, on a fixed-timestep
 * accumulator driven by the frame loop.
 *
 * This used to be a `setInterval` at the tick rate, and that was the choppiness in
 * your own character: browsers throttle and coalesce timers, so commands came out
 * in uneven clumps, the predictor stepped in those same clumps, and the view moved
 * in visible stutters no matter how smooth the simulation underneath was. An
 * accumulator measured against real time produces a command per tick of wall clock
 * regardless of when frames happen to land.
 *
 * Frame rate still cannot change how the game feels: the number of commands is set
 * by elapsed TIME, not by the number of frames. A slow frame emits two, a fast one
 * emits none, and the leftover carries over.
 *
 * Each command is predicted the instant it is built and sent afterwards. That
 * order is the whole trick — the local view never waits for the network, and the
 * simulated-lag delay queue in `net.ts` therefore delays only the correction.
 */
function pumpInput(rawNow: number, render: number, predictor: Predictor): void {
  const session = sending;
  if (session === undefined) return;

  const step = tickMs();
  if (lastPumpAt === undefined) {
    lastPumpAt = rawNow;
    simTime = render;
    return;
  }
  // The REAL clock, never the render clock: hitstop must not stall input
  // (AGENTS.md: contacts are sounded from the server).
  pumpAcc += rawNow - lastPumpAt;
  lastPumpAt = rawNow;

  // A backgrounded tab stops rAF entirely and comes back with seconds banked.
  // Firing hundreds of commands at once would spend the whole prediction buffer
  // on input the player never gave; the server has been holding the last one.
  if (pumpAcc > step * MAX_PUMP_CATCHUP) pumpAcc = step * MAX_PUMP_CATCHUP;

  while (pumpAcc >= step) {
    pumpAcc -= step;
    const command = input.nextCommand();
    // The render-clock instant this virtual tick belongs to. Feeding the raw frame
    // time instead would collapse two ticks produced in one frame onto the same
    // timestamp, and the sub-tick smoothing would read zero for both.
    simTime = render - pumpAcc;
    predictor.record(command, simTime);
    session(command);
  }
}

/**
 * Tab opens the scoreboard. It is bound here rather than through the debug
 * keybinds because it must swallow the keypress: Tab is the browser's focus key,
 * and letting it through walks focus out of the canvas and drops mouselook.
 */
function bindScoreboardKey(scoreboard: Scoreboard): void {
  window.addEventListener('keydown', (ev) => {
    // Bound by hand rather than through `onKeyPress`, so the text-entry guard that
    // helper applies has to be repeated here.
    if (ev.code !== 'Tab' || ev.repeat || isTextEntryTarget(ev.target)) return;
    ev.preventDefault();
    scoreboard.toggle();
  });
}

function bindDebugKeys(
  renderSync: RenderSync,
  hud: Hud,
  chat: Chat,
  trajectories: TrajectoryView,
): void {
  onKeyToggle('KeyK', (on) => {
    stage.setColliderWireframe(on);
    // The player hurtbox capsule, NOT a wireframe pass over the character.
    // Wireframing the whole figure used to be the only way to see what the
    // server tests against, and it answered the question badly — a mesh of
    // armour lines is not a hitbox, and reading one as the other is exactly the
    // confusion the drawn capsule exists to end.
    renderSync.setHitboxes(on);
    chat.system(`colliders and limb hurtboxes ${on ? 'on' : 'off'}`);
  });
  onKeyToggle('F3', (on) => hud.setDebugVisible(on));
  onKeyPress('KeyV', () => {
    firstPerson = !firstPerson;
    // Your own body would fill the screen from inside its own head.
    renderSync.setSelfBodyVisible(!firstPerson);
    chat.system(firstPerson ? 'first person' : 'third person');
  });
  onKeyPress('KeyJ', () => {
    const clip = trajectories.cycle();
    chat.system(clip === undefined ? 'trajectory preview off' : `trajectory: ${clip}`);
  });
  onKeyToggle('KeyM', (on) => {
    audio.setMuted(on);
    chat.system(on ? 'sound muted' : 'sound on');
  });
  onKeyPress('KeyN', () => {
    const sound = AUDITION[auditionAt % AUDITION.length]!;
    auditionAt += 1;
    sound.play();
    chat.system(`sound: ${sound.name}`);
  });
}

void run();
